import axios from 'axios';
import CryptoJS from 'crypto-js';

// Configuration
const API_URL = process.env.REACT_APP_PAYLOAD_URL || 'http://localhost:3000';
const AUTH_TOKEN = process.env.REACT_APP_AUTH_TOKEN;
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to encrypt and send Bearer token
api.interceptors.request.use(
  (config) => {
    if (AUTH_TOKEN && ENCRYPTION_KEY) {
      try {
        // Encrypt the auth token using AES (CryptoJS format)
        const encryptedToken = CryptoJS.AES.encrypt(AUTH_TOKEN, ENCRYPTION_KEY).toString();

        // Add encrypted token as Bearer token in Authorization header
        config.headers['Authorization'] = `Bearer ${encryptedToken}`;
            
      } catch (error) {
        console.error('Error auth token:', error);
      }
    }

    return config;
  },  
  (error) => {
    console.error('❌ [REQUEST] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => {
    // Successful response, pass through
    return response;
  },
  (error) => {
    // Handle authentication errors (401 Unauthorized)
    if (error.response?.status === 401) {
      console.error(' Authentication failed (401 Unauthorized)');
    }

    // Handle other errors
    if (error.response) {
      // Server responded with error status
      console.error(`❌ [API] Server error (${error.response.status}):`, error.response.data);
    } else if (error.request) {
      // Request made but no response received
      console.error('❌ [API] No response from server:', error.message);
    } else {
      // Error setting up the request
      console.error('❌ [API] Request setup error:', error.message);
    }

    return Promise.reject(error);
  }
);

/**
 * Helper function to get media URL
 * Payload stores media with full URL or relative path
 */
export const getPayloadMedia = (media) => {
  if (!media) return null;

  // If media is an object with url property
  if (typeof media === 'object' && media.url) {
    // If URL is already absolute, return as-is
    if (media.url.startsWith('http')) {
      return media.url;
    }
    // Otherwise, prepend the API URL
    return `${API_URL}${media.url}`;
  }

  // If media is a string (URL)
  if (typeof media === 'string') {
    if (media.startsWith('http')) {
      return media;
    }
    return `${API_URL}${media}`;
  }

  return null;
};

/**
 * Transform Payload response to match Strapi format (optional)
 * Payload response is already clean: { id, field1, field2, ... }
 */
export const transformPayloadResponse = (data) => {
  if (!data) return null;

  // Payload response is already in a clean format
  // No need for transformation like Strapi's { data: { attributes: {} } }
  return data;
};

// ============================================
// LANDING PAGES
// ============================================

/**
 * Get all landing pages
 */
export const getLandingPages = async () => {
  try {
    const response = await api.get('/landing-pages', {
      params: {
        where: {
          activo: { equals: true },
        },
        depth: 2,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching landing pages:', error);
    throw error;
  }
};

/**
 * Get landing page by slug
 */
export const getLandingPagePorSlug = async (slug) => {
  try {
    const response = await api.get('/landing-pages', {
      params: {
        where: {
          slug: { equals: slug },
        },
        depth: 2,
      },
    });
    return response.data.docs[0] || null;
  } catch (error) {
    console.error('Error fetching landing page by slug:', error);
    throw error;
  }
};

/**
 * Get the first active landing page
 */
export const getLandingPageActiva = async () => {
  try {
    const response = await api.get('/landing-pages', {
      params: {
        where: {
          activo: { equals: true },
        },
        depth: 2,
        limit: 1,
      },
    });
    return response.data.docs[0] || null;
  } catch (error) {
    console.error('Error fetching active landing page:', error);
    throw error;
  }
};

// ============================================
// ARTICLES/BLOG
// ============================================

/**
 * Get published articles
 */
export const getArticulos = async (limite = 10) => {
  try {
    const response = await api.get('/articulos', {
      params: {
        where: {
          publicado: { equals: true },
        },
        depth: 1,
        limit: limite,
        sort: '-fechaPublicacion',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching articles:', error);
    throw error;
  }
};

/**
 * Get article by slug
 */
export const getArticuloPorSlug = async (slug) => {
  try {
    const response = await api.get('/articulos', {
      params: {
        where: {
          slug: { equals: slug },
        },
        depth: 1,
      },
    });
    return response.data.docs[0] || null;
  } catch (error) {
    console.error('Error fetching article by slug:', error);
    throw error;
  }
};

// ============================================
// PRODUCTS
// ============================================

/**
 * Get products with optional filters
 */
export const getProductos = async (filtros = {}) => {
  try {
    const params = {
      depth: 1,
      ...filtros,
    };

    const response = await api.get('/productos', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

/**
 * Get product by ID
 */
export const getProducto = async (id) => {
  try {
    const response = await api.get(`/productos/${id}`, {
      params: {
        depth: 1,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};

// ============================================
// CONFIGURATION & THEME
// ============================================

/**
 * Get site configuration
 */
export const getConfiguracion = async () => {
  try {
    const response = await api.get('/globals/configuracion', {
      params: {
        depth: 1,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching configuration:', error);
    throw error;
  }
};

/**
 * Get theme colors
 */
export const getTema = async () => {
  try {
    const response = await api.get('/globals/tema');
    return response.data;
  } catch (error) {
    console.error('Error fetching theme:', error);
    throw error;
  }
};

// ============================================
// BOOKING & PAYMENTS
// ============================================

/**
 * Create a new reservation
 */
export const crearReserva = async (datosReserva) => {
  try {
    const response = await api.post('/reservas/crear', datosReserva);
    return response.data;
  } catch (error) {
    console.error('❌ Error creating reservation:', error);
    console.error('❌ Response status:', error.response?.status);
    console.error('❌ Response data:', error.response?.data);
    throw error;
  }
};

/**
 * Create a payment record
 */
export const crearPago = async (datosPago) => {
  try {
    const response = await api.post('/pagos', datosPago);
    return response.data;
  } catch (error) {
    console.error('Error creating payment:', error);
    throw error;
  }
};

/**
 * Update payment
 */
export const actualizarPago = async (id, datos) => {
  try {
    const response = await api.patch(`/pagos/${id}`, datos);
    return response.data;
  } catch (error) {
    console.error('Error updating payment:', error);
    throw error;
  }
};

// ============================================
// EPAYCO INTEGRATION
// ============================================

/**
 * Create ePayco payment session
 */
export const crearSesionEpayco = async (datosPago) => {
  try {
    const response = await api.post('/epayco/create-session', datosPago);
    return response.data;
  } catch (error) {
    console.error('Error creating ePayco session:', error);
    throw error;
  }
};

/**
 * Get payment status by reference
 */
export const obtenerEstadoPago = async (reference) => {
  try {
    const response = await api.get('/epayco/status', {
      params: { reference },
    });
    return response.data;
  } catch (error) {
    console.error('Error getting payment status:', error);
    throw error;
  }
};

// Export default service object
const payloadService = {
  getPayloadMedia,
  transformPayloadResponse,
  getLandingPages,
  getLandingPagePorSlug,
  getLandingPageActiva,
  getArticulos,
  getArticuloPorSlug,
  getProductos,
  getProducto,
  getConfiguracion,
  getTema,
  crearReserva,
  crearPago,
  actualizarPago,
  crearSesionEpayco,
  obtenerEstadoPago,
};

export default payloadService;
